import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { State } from '../../../shared/core/state';

import { Resource, ResourceUploadStatus } from '../../../shared/core/state/resource/models/index';
import * as ResourceActions from '../../../shared/core/state/resource/resource.actions';
import { ToastService } from '@black/ui';

@Component({
  selector: 'black-course-upload',
  templateUrl: './course-upload.component.html',
  styleUrls: ['./course-upload.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class CourseUploadComponent implements OnInit, OnDestroy {
  private store = inject<Store<State>>(Store);
  private toast = inject(ToastService);
  private destroy$ = new Subject<void>();

  @Input()
  selectedCourseId$: Observable<string | undefined>;
  selectedCourseId = '';

  activeTab: 'files' | 'upload' = 'files';
  searchQuery = '';

  uploadedFiles$;

  constructor() {
    this.selectedCourseId$ = new Observable();
    this.uploadedFiles$ = this.store.pipe(
      select((store) => store.resources.resourceItems)
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(ResourceActions.stopPollingResourceItem());
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.selectedCourseId$.subscribe((courseId) => {
      if (courseId) {
        this.selectedCourseId = courseId;
        this.fetchCourseResources(courseId, true);
      }
    });
  }

  filterFiles(files: Resource[] | null): Resource[] {
    if (!files) return [];
    if (!this.searchQuery.trim()) return files;
    const q = this.searchQuery.toLowerCase().trim();
    return files.filter((f) => f.name?.toLowerCase().includes(q));
  }

  getFileIcon(type?: string, name?: string): string {
    const t = (type || '').toLowerCase();
    const n = (name || '').toLowerCase();
    if (t === 'video' || n.endsWith('.mp4') || n.endsWith('.mkv') || n.endsWith('.mov')) {
      return 'fa-solid fa-video text-primary text-sm';
    }
    if (t === 'document' || n.endsWith('.pdf') || n.endsWith('.doc') || n.endsWith('.docx') || n.endsWith('.ppt') || n.endsWith('.pptx')) {
      return 'fa-solid fa-file-lines text-info text-sm';
    }
    if (t === 'image' || n.endsWith('.png') || n.endsWith('.jpg') || n.endsWith('.jpeg')) {
      return 'fa-solid fa-image text-success text-sm';
    }
    return 'fa-solid fa-file text-base-content/50 text-sm';
  }

  copyResourceId(id: string): void {
    if (!id) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(id).then(() => {
        this.toast.showToast('Resource ID copied', 'success');
      }).catch(() => {
        this.toast.showToast('Could not copy ID', 'error');
      });
    } else {
      this.toast.showToast(`ID: ${id}`, 'info');
    }
  }

  generateLabelForResource(status: ResourceUploadStatus | undefined) {
    switch (status) {
      case ResourceUploadStatus.UPLOADING:
        return 'UPLOADING';
      case ResourceUploadStatus.COMPLETED:
        return 'COMPLETED';
      case ResourceUploadStatus.PROCESSING:
        return 'PROCESSING';
      case ResourceUploadStatus.CANCELED:
        return 'CANCELED';
      case ResourceUploadStatus.FAILED:
        return 'FAILED';
      default:
        return status || '';
    }
  }

  fetchCourseResources(courseId: string, isAutoSync = false) {
    if (courseId && courseId !== '') {
      if (isAutoSync) {
        this.store.dispatch(
          ResourceActions.pollResourceItemsByCourse({
            courseId: courseId,
          })
        );
      } else {
        this.store.dispatch(
          ResourceActions.fetchResourceItemsByCourse({
            courseId: courseId,
          })
        );
      }
    }
  }
}
